import { getItemDisplayName } from '../../helpers/adminName';
import { interpolateExperienceText } from '../../../../services/set/interpolation';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_OBJ, NOOP } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  AutomationStatus,
  BatchMode,
  ExperienceAnchorType,
  ExperienceStatus,
  ExperienceTemplateMessage,
  IAutomationTemplate,
  IExperience,
  IExperienceTemplate,
  IExperienceTemplateResource,
  ITaskTemplate,
  ITimelineItem,
  MessageStatus,
  PARTICIPANT_ACTOR_KEY,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { ScheduleChannel } from '@dadohr/shared/domainTypes/schedules';
import styled from '@emotion/styled';
import { Button, Dialog, Typography } from '@material-ui/core';
import { assertNever } from 'assert-never';
import { compact, orderBy, partition } from 'lodash';
import {
  AlignStartVertical,
  Calendar,
  CheckCircle2,
  Clock,
  FileOutput,
  List,
  User,
  UserPlus,
  Users,
  Waypoints,
  Zap
} from 'lucide-react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogActionsHeader } from '../../../../components/DialogActionsHeader';
import { DialogContentWithoutPadding } from '../../../../components/DialogContentWithoutPadding';
import {
  Controls as ExperienceTimelineControls,
  ExperienceTimeline,
  Legend as ExperienceTimelineLegend,
  setLanesCollapsedView,
  toggleHideLabelsView,
  useExperienceTimelineViewState,
  zoomInView,
  zoomOutView
} from '../../../../components/ExperienceTimeline';
import { FixedPopover } from '../../../../components/Popover';
import {
  Timeline,
  TimelineAnnotation,
  TimelineItem,
  TimelineItem2
} from '../../../../components/Timeline';
import {
  TabPanel,
  TimelineDialogTabs,
  useTimelineDialogTabQueryParam
} from '../../../../components/TimelineDialogTabs';
import { useHover } from '../../../../hooks/useHover';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import {
  SubHeadlineContainer,
  SubHeadLineItem
} from '../../../../layout/SubHeadlineContainer';
import { useQueryParam } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { useNewUx } from '../../../../services/newUx';
import {
  AutomationStats,
  getAutomationsStats
} from '../../../../services/experiences/automation';
import {
  useExperienceInstancesCurrentlyRelevant,
  useInstanceStatsByTemplateId
} from '../../../../services/experiences/instance';
import {
  usePeopleTabV2Enabled,
  useTimelineItemPseudoInstances,
  useTimelineItemStatsByTemplateId
} from '../../../../services/experiences/timelineItemStatus';
import { getTaskStats, TaskStats } from '../../../../services/experiences/task';
import {
  getActorSortKey,
  getAllMessageTemplates,
  getMessageBatchConnections,
  getTemplateDuration,
  getTriggerConnectors,
  getTriggerHierarchyByEffect,
  useTriggeredItemsForTask
} from '../../../../services/experiences/template';
import {
  getTimelinePositionForAutomationTemplate,
  getTimelinePositionForMessageTempate,
  getTimelinePositionForTaskTemplate
} from '../../../../services/experiences/timing';
import { useMappedLoadingValue } from '../../../../services/firebase';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { humanizeDuration, ONE_DAY, toMoment } from '../../../../services/time';
import { getWeekLabel } from '../../../../services/experiences/weekLabels';
import { FullTheme, useTheme } from '../../../../themes';
import {
  ActorDescriptor,
  ActorLegendV2,
  useActorDescriptorsFromTemplate
} from '../../components/Actors';
import {
  getTemplateAudienceCarriers,
  getVisibleItemIdsForSegment,
  SegmentDescriptor
} from '../../components/ExperienceTimeline/segmentFilter';
import { SegmentFilterRow, useTemplateSegments } from '../../components/Segments';
import {
  AnchorField,
  useAllRelevantAnchorRulers
} from '../../components/Anchors';
import {
  AutomationBuilderReadOnly,
  AutomationViewerNewUxShell
} from '../../components/Builders/AutomationBuilder';
import {
  TaskBuilderReadOnly,
  TaskViewerNewUxShell
} from '../../components/Builders/TaskBuilder';
import { useTokenDefinitionsForExperience } from '../../components/Builders/TaskBuilder/components/BlockSettings/common';
import { useTaskBuilderContext } from '../../components/Builders/TaskBuilder/types';
import { ChannelIcons } from '../../components/Channels';
import { GoToTimelineElementEditButton } from '../../components/GoToTimelineElementEditButton';
import { MessageItem2 } from '../../components/Messages';
import {
  TaskTimelineZoomControls,
  TaskTimelineZoomControlsContainer,
  useTaskTimelineScaleChangeRequest
} from '../../components/TaskTimelineZoomControls';
import {
  AutomationExecutionReportTab,
  TaskExecutionReportTab
} from './ExecutionReportTab';
import { TimelineItemNotesTab } from './TimelineItemNotesTab';
import { SendTestTaskButton } from './SendTest';
import { TaskTimelineAnnotationPopoverBody } from './TaskTimelineAnnotationPopoverBody';
import { parseTimelineItemIdParam, TIMELINE_ITEM_QUERY_PARAM, toTimelineItemIdParam } from './timelineItemId';
import {
  TaskTimelineAutomationPopoverBody,
  TaskTimelineTaskPopoverBody
} from './TaskTimelinePopoverBody';
import { mergeAnnotationBuckets } from '../../components/ExperienceTimeline/annotationBuckets';
import { TimelineInspectorRail } from '../../components/ExperienceTimeline/TimelineInspectorRail';
import { useExperienceTimelineModel } from '../../components/ExperienceTimeline/useExperienceTimelineModel';

// TODO: Remove once we have the export list functionality for report execution implemented
const isExportList = false;

export const getTimelineTaskColor = (
  colors: FullTheme['custom']['colors'],
  status: TaskStatus
): {
  light: { text: string; bg: string };
  normal: { text: string; bg: string };
} => {
  switch (status) {
    case TaskStatus.NOT_STARTED:
      return {
        light: { text: 'inherit', bg: colors.gray.w200 },
        normal: { text: 'inherit', bg: colors.gray.w300 }
      };
    case TaskStatus.RUNNING:
      return {
        light: { text: 'inherit', bg: colors.blue.w200 },
        normal: { text: colors.blue.contrastText, bg: colors.blue.w400 }
      };
    case TaskStatus.LATE:
      return {
        light: { text: 'inherit', bg: colors.red.w200 },
        normal: { text: colors.red.contrastText, bg: colors.red.w400 }
      };
    case TaskStatus.COMPLETE:
      return {
        light: { text: 'inherit', bg: colors.green.w200 },
        normal: { text: colors.green.contrastText, bg: colors.green.w400 }
      };
    case TaskStatus.SKIPPED:
      return {
        light: { text: 'inherit', bg: colors.gray.w300 },
        normal: { text: 'inherit', bg: colors.gray.w400 }
      };
    case TaskStatus.PAST_DEADLINE:
      return {
        light: { text: 'inherit', bg: colors.gray.w300 },
        normal: { text: 'inherit', bg: colors.gray.w400 }
      };
    default:
      return assertNever(status);
  }
};

export const getTimelineMessageColor = (
  colors: FullTheme['custom']['colors'],
  status: MessageStatus | null
): {
  light: { text: string; bg: string };
  normal: { text: string; bg: string };
} => {
  switch (status) {
    case MessageStatus.ERROR:
      return {
        light: { text: 'inherit', bg: colors.red.w200 },
        normal: { text: colors.red.contrastText, bg: colors.red.w400 }
      };
    case MessageStatus.DONE:
      return {
        light: { text: 'inherit', bg: colors.green.w200 },
        normal: { text: colors.green.contrastText, bg: colors.green.w400 }
      };
    case MessageStatus.SKIPPED:
      return {
        light: { text: 'inherit', bg: colors.gray.w300 },
        normal: { text: 'inherit', bg: colors.gray.w400 }
      };
    default:
      // null or MessageStatus.UPCOMING
      return {
        light: { text: 'inherit', bg: colors.gray.w300 },
        normal: { text: 'inherit', bg: colors.gray.w400 }
      };
  }
};

export const getTimelineAutomationColor = (
  colors: FullTheme['custom']['colors'],
  status: AutomationStatus
): {
  light: { text: string; bg: string };
  normal: { text: string; bg: string };
} => {
  switch (status) {
    case AutomationStatus.UPCOMING:
    case AutomationStatus.TRIGGERED:
      return {
        light: { text: 'inherit', bg: colors.gray.w200 },
        normal: { text: 'inherit', bg: colors.gray.w300 }
      };
    case AutomationStatus.ERROR:
      return {
        light: { text: 'inherit', bg: colors.red.w200 },
        normal: { text: colors.red.contrastText, bg: colors.red.w400 }
      };
    case AutomationStatus.DONE:
      return {
        light: { text: 'inherit', bg: colors.green.w200 },
        normal: { text: colors.green.contrastText, bg: colors.green.w400 }
      };
    case AutomationStatus.SKIPPED:
      return {
        light: { text: 'inherit', bg: colors.gray.w300 },
        normal: { text: 'inherit', bg: colors.gray.w400 }
      };
    default:
      return assertNever(status);
  }
};

type Variant = 'dense' | 'normal' | 'placeholder';

export const withCustomizedTimingHint = (
  customizedTiming: boolean | undefined,
  otherElements: React.ReactNode
) => {
  return customizedTiming ? (
    <FlexContainer>
      <Clock size={14} />
      {otherElements}
    </FlexContainer>
  ) : (
    otherElements
  );
};

export const TaskTimelineItem = React.forwardRef<
  HTMLDivElement,
  {
    title: React.ReactNode;
    channels: ScheduleChannel[];
    actor: ActorDescriptor | undefined;
    variant?: Variant;
    connector?: 'L' | 'T';
    onClick?: () => void;

    fontColor?: string;
    bgColor?: string;
    transparent?: boolean;
    deactivated?: boolean;
    style?: React.CSSProperties;

    selected?: boolean;
    onSelect?: (nextSelected: boolean) => void;
    customizedTiming?: boolean;
    selectMenu?: React.ReactNode;
    hideChannelIcons?: boolean;

    connectUp?: boolean;
    connectDown?: boolean;
    connectRight?: boolean;
    connectLeft?: 'up' | 'down' | 'upAndDown';
  }
>(
  (
    {
      title,
      actor,
      channels,
      variant,
      connector,
      onClick,
      fontColor,
      bgColor,
      transparent,
      deactivated,
      style,
      selected,
      onSelect,
      customizedTiming,
      selectMenu,
      hideChannelIcons,

      connectUp,
      connectDown,
      connectLeft,
      connectRight
    },
    ref
  ) => {
    return (
      <TimelineItem2
        ref={ref}
        variant={variant}
        title={title}
        onClick={onClick}
        connector={connector}
        fontColor={fontColor}
        bgColor={bgColor}
        leftColor={actor?.color}
        leftIcon={CheckCircle2}
        right={withCustomizedTimingHint(
          customizedTiming,
          !hideChannelIcons && <ChannelIcons size={16} channels={channels} />
        )}
        style={style}
        selected={selected}
        onSelect={onSelect}
        transparent={transparent || deactivated}
        selectMenu={selectMenu}
        connectUp={connectUp}
        connectDown={connectDown}
        connectLeft={connectLeft}
        connectRight={connectRight}
      />
    );
  }
);

const TaskTimelineItemWithStats = ({
  tpl,
  stats,
  actor,
  variant = 'normal',
  connector,
  onClick,
  expTitle,
  orgName,

  connectUp,
  connectDown,
  connectLeft,
  connectRight
}: {
  tpl: ITaskTemplate;
  stats: TaskStats | null;
  actor: ActorDescriptor;
  variant?: Variant;
  connector?: 'L' | 'T';
  onClick?: () => void;
  expTitle?: string;
  orgName?: string;

  connectUp?: boolean;
  connectDown?: boolean;
  connectRight?: boolean;
  connectLeft?: 'up' | 'down' | 'upAndDown';
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const hovered = useHover(ref);
  const displayTitle = expTitle
    ? interpolateExperienceText(
        getItemDisplayName(tpl),
        {
          experience: { vars: { title: expTitle, tasks: {} } },
          org: { vars: { name: orgName || '' } },
          roles: []
        },
        { dateFormat: 'YYYY/MM/DD' }
      )
    : getItemDisplayName(tpl);
  return (
    <>
      <TaskTimelineItem
        ref={ref}
        title={displayTitle}
        channels={tpl.channels}
        actor={actor}
        connector={connector}
        variant={variant}
        deactivated={tpl.deactivated}
        onClick={onClick}
        connectUp={connectUp}
        connectDown={connectDown}
        connectLeft={connectLeft}
        connectRight={connectRight}
      />
      {variant === 'normal' && (
        <FixedPopover open={hovered} anchor={ref.current}>
          <TaskTimelineTaskPopoverBody tpl={tpl} stats={stats} actor={actor} expTitle={expTitle} orgName={orgName} />
        </FixedPopover>
      )}
    </>
  );
};

export const AutomationTimelineItem = React.forwardRef<
  HTMLDivElement,
  {
    tpl: IAutomationTemplate;
    variant?: Variant;

    fontColor?: string;
    bgColor?: string;
    transparent?: boolean;
    onClick?: () => void;

    selected?: boolean;
    onSelect?: (nextSelected: boolean) => void;
    customizedTiming?: boolean;
    selectMenu?: React.ReactNode;
    style?: React.CSSProperties;

    connectUp?: boolean;
    connectDown?: boolean;
    connectRight?: boolean;
    connectLeft?: 'up' | 'down' | 'upAndDown';
  }
>(
  (
    {
      tpl,
      variant,
      fontColor,
      bgColor,
      transparent,
      onClick,
      selected,
      onSelect,
      customizedTiming,
      selectMenu,
      style,

      connectUp,
      connectDown,
      connectLeft,
      connectRight
    },
    ref
  ) => {
    const theme = useTheme();
    return (
      <TimelineItem2
        ref={ref}
        variant={variant}
        title={tpl.title}
        onClick={onClick}
        fontColor={fontColor}
        bgColor={bgColor}
        transparent={transparent || tpl.deactivated}
        leftColor={theme.custom.colors.purple.w500}
        leftIcon={Zap}
        right={withCustomizedTimingHint(customizedTiming, null)}
        selected={selected}
        onSelect={onSelect}
        selectMenu={selectMenu}
        style={style}
        connectUp={connectUp}
        connectDown={connectDown}
        connectLeft={connectLeft}
        connectRight={connectRight}
      />
    );
  }
);

const AutomationTimelineItemWithSats = ({
  tpl,
  stats = null,
  variant = 'normal',
  onClick,
  connectUp,
  connectDown,
  connectLeft,
  connectRight
}: {
  tpl: IAutomationTemplate;
  stats?: AutomationStats | null;
  variant?: Variant;
  onClick?: () => void;

  connectUp?: boolean;
  connectDown?: boolean;
  connectRight?: boolean;
  connectLeft?: 'up' | 'down' | 'upAndDown';
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const isHovered = useHover(ref);

  return (
    <>
      <AutomationTimelineItem
        ref={ref}
        tpl={tpl}
        variant={variant}
        onClick={onClick}
        connectUp={connectUp}
        connectDown={connectDown}
        connectLeft={connectLeft}
        connectRight={connectRight}
      />
      {variant === 'normal' && (
        <FixedPopover open={isHovered} anchor={ref.current}>
          <TaskTimelineAutomationPopoverBody tpl={tpl} stats={stats} />
        </FixedPopover>
      )}
    </>
  );
};

const TimelineSectionContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spacing(2)}px;
`;

const TimelineModeToggle = styled('div')`
  display: inline-flex;
  align-self: flex-start;
  margin: ${(p) => p.theme.spacing(1)}px 0 ${(p) => p.theme.spacing(3)}px;
  border: 1px solid ${(p) => p.theme.custom.colors.gray.w300};
  border-radius: ${(p) => p.theme.custom.border.radius()}px;
  overflow: hidden;
`;

const TimelineModeButton = styled('button')<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing(1)}px;
  padding: ${(p) => p.theme.spacing(1)}px ${(p) => p.theme.spacing(2)}px;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 15px;
  font-weight: 500;
  line-height: 1;
  background: ${(p) =>
    p.active ? p.theme.palette.primary.main : p.theme.custom.colors.gray.white};
  color: ${(p) =>
    p.active
      ? p.theme.palette.primary.contrastText
      : p.theme.custom.colors.gray.w900};
  & + & {
    border-left: 1px solid ${(p) => p.theme.custom.colors.gray.w300};
  }
`;

const TimelineModeSwitcher = ({
  useNewTimeline,
  onChange
}: {
  useNewTimeline: boolean;
  onChange: (useNewTimeline: boolean) => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <TimelineModeToggle role="group">
      <TimelineModeButton
        type="button"
        active={useNewTimeline}
        aria-pressed={useNewTimeline}
        onClick={() => onChange(true)}
      >
        <Waypoints size={16} />
        {t('Process map')}
      </TimelineModeButton>
      <TimelineModeButton
        type="button"
        active={!useNewTimeline}
        aria-pressed={!useNewTimeline}
        onClick={() => onChange(false)}
      >
        <AlignStartVertical size={16} />
        {t('Gantt')}
      </TimelineModeButton>
    </TimelineModeToggle>
  );
};

type EnrollStatsProps = { orgId: string; tplId: string; expectedEndThreshold: number };

const EnrollStatsView = ({
  stats
}: {
  stats: { todo: number; overdue: number; done: number; upcoming: number } | void;
}) => {
  const { t } = useTranslation();
  if (!stats) {
    return null;
  }
  const enrolled = stats.todo + stats.overdue + stats.done;
  const preEnrolled = stats.upcoming;
  return (
    <>
      <SubHeadLineItem>
        <Users size="16" /> {t('participantsWithCount', { count: enrolled })}{' '}
        {t('enrolled')}{' '}
      </SubHeadLineItem>
      <SubHeadLineItem>
        <UserPlus size="16" />{' '}
        {t('participantsWithCount', { count: preEnrolled })} {t('upcoming')}{' '}
      </SubHeadLineItem>
    </>
  );
};

const EnrollStatsV1 = ({ orgId, tplId, expectedEndThreshold }: EnrollStatsProps) => {
  const [stats] = useInstanceStatsByTemplateId(orgId, tplId, expectedEndThreshold);
  return <EnrollStatsView stats={stats} />;
};

const EnrollStatsV2 = ({ orgId, tplId, expectedEndThreshold }: EnrollStatsProps) => {
  const [stats] = useTimelineItemStatsByTemplateId(orgId, tplId, expectedEndThreshold);
  return <EnrollStatsView stats={stats} />;
};

const EnrollStats = (props: EnrollStatsProps) =>
  usePeopleTabV2Enabled() ? <EnrollStatsV2 {...props} /> : <EnrollStatsV1 {...props} />;

const Annotation = ({
  instances,
  tpl,
  anchorFields,
  variant = 'normal'
}: {
  instances: Doc<IExperience>[];
  tpl: Doc<IExperienceTemplate>;
  anchorFields: AnchorField[];
  variant?: Variant;
}) => {
  const ref = useRef<HTMLButtonElement>(null);
  const hovered = useHover(ref);
  // TODO - open TaskTimelineAnnotationModal on click
  return (
    <>
      <Button
        style={{ boxShadow: '0px 0px 0px 1px white', background: 'white' }}
        variant="outlined"
        size="small"
        ref={ref}
        startIcon={
          instances.length > 1 ? (
            <Users size={16} />
          ) : instances.length === 1 ? (
            <User size={16} />
          ) : null
        }
      >
        {instances.length}
      </Button>
      {variant === 'normal' && (
        <FixedPopover open={hovered} anchor={ref.current}>
          <TaskTimelineAnnotationPopoverBody
            instances={instances}
            tpl={tpl}
            anchorFields={anchorFields}
          />
        </FixedPopover>
      )}
    </>
  );
};

const getAnnotationStart = (
  d: Doc<IExperience>,
  tpl: Doc<IExperienceTemplate>
) => {
  const { anchor } = tpl.data;

  const instanceAnchorMs = toMoment(d.data.anchorDate.at)
    .tz(tpl.data.tz)
    .startOf('d')
    .valueOf();
  if (
    anchor.type === ExperienceAnchorType.EMPLOYEE_DATE &&
    anchor.offset.type === 'ABSOLUTE' &&
    anchor.offset.duration < 0
  ) {
    const virtualStartDateMs = instanceAnchorMs + anchor.offset.duration; // duration is a negative value
    return Date.now() - virtualStartDateMs;
  }
  // start of day???
  return Date.now() - instanceAnchorMs;
};

export const sortTaskTimelineItems = (ts: TimelineItem[]) => {
  return orderBy(ts, [
    (t) => {
      // this is here, so that items that are triggered by the same task are next to each other
      return t.parents?.join(',') || '';
    },
    (t) => {
      const actor = t.data?.actor;
      return getActorSortKey(actor || null);
    },
    (t) => t.start,
    (t) => {
      const type = t.data?.type || null;
      return type === 'MESSAGE' ? 0 : type === 'TASK' ? 1 : 2;
    },
    (t) => {
      const batchMode: BatchMode | null = t.data?.batchMode || null;
      const isMsg = t.data?.type === 'MESSAGE';
      return batchMode === BatchMode.DEFAULT
        ? isMsg
          ? 15
          : 5
        : batchMode === BatchMode.PER_COHORT
        ? 10
        : 0;
    }
  ]);
};

export const TaskTemplateReadOnlyDialog = ({
  open,
  onClose,
  tpl,
  expTpl,
  anchorFields,
  initialModalTab = 'item',
  onGoToOtherItem
}: {
  open: boolean;
  onClose: () => void;
  tpl: ITaskTemplate;
  expTpl: Doc<IExperienceTemplate>;
  anchorFields: AnchorField[];
  initialModalTab?: 'item' | 'people';
  onGoToOtherItem?: (item: ITimelineItem) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const expTplMini = useTaskBuilderContext(expTpl.data);
  const triggeredItems = useTriggeredItemsForTask(expTpl, tpl.id);
  const { org } = useCurrentUser();
  const [previewTab, setPreviewTab] = useTimelineDialogTabQueryParam(
    'tab',
    initialModalTab
  );

  const tokens = useTokenDefinitionsForExperience(
    expTplMini.supportingActors,
    org.customUserFields,
    { orgId: expTplMini.orgId, experienceTemplateId: expTplMini.templateId }
  );

  const { enabled: newUxEnabled } = useNewUx();

  const close = () => {
    setPreviewTab(null);
    onClose();
  };

  // Block selected in the viewer; carried into the edit URL by the Edit button.
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);

  // NEW_UX: the tabbed read-only dialog becomes the icon-rail task viewer —
  // the Task/People/Notes tabs are re-housed as rail sections. Classic
  // (newUx off) keeps the exact tabbed layout below.
  if (newUxEnabled) {
    return (
      <Dialog open={open} onClose={close} maxWidth={'xl'} fullWidth>
        {open && (
          <DialogContentWithoutPadding fullHeight>
            <TaskViewerNewUxShell
              value={tpl}
              onSelectedBlockChange={setSelectedBlockId}
              expTpl={expTplMini}
              resources={expTpl.data.resources}
              validation={EMPTY_OBJ}
              anchorFields={anchorFields}
              showOnCompleteHookSettings={true}
              tokens={tokens}
              triggeredItems={triggeredItems}
              onGoToOtherItem={onGoToOtherItem}
              onCloseDialog={close}
              editButton={
                <GoToTimelineElementEditButton
                  expTpl={expTpl}
                  type="TASK"
                  elementId={tpl.id}
                  title={tpl.title}
                  selectedBlockId={selectedBlockId}
                />
              }
              recipientsPanel={
                <TaskExecutionReportTab
                  tpl={expTpl}
                  taskTplId={tpl.id}
                  taskTplTitle={tpl.title}
                  newUx
                />
              }
              notesPanel={
                <TimelineItemNotesTab
                  orgId={expTpl.data.orgId}
                  experienceTemplateId={expTpl.id}
                  itemId={tpl.id}
                  itemType="TASK"
                />
              }
            />
          </DialogContentWithoutPadding>
        )}
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={close} maxWidth={'xl'} fullWidth>
      {open && (
        <>
          <DialogActionsHeader
            hasVerticalPadding={false}
            onClose={close}
            left={
              <TimelineDialogTabs
                onChange={setPreviewTab}
                label={t('Task')}
                previewTab={previewTab}
                elementId={tpl.id}
                title={tpl.title}
                type="TASK"
                selectedTab="Task"
                experienceTemplateId={expTpl.id}
              />
            }
          >
            {previewTab === 'item' && (
              <GoToTimelineElementEditButton
                expTpl={expTpl}
                type="TASK"
                elementId={tpl.id}
                title={tpl.title}
                selectedBlockId={selectedBlockId}
              />
            )}
            {isExportList && previewTab === 'people' && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileOutput size={16} />}
              >
                Export list
              </Button>
            )}
          </DialogActionsHeader>
          <DialogContentWithoutPadding fullHeight>
            <TabPanel active={previewTab === 'item'}>
              <TaskBuilderReadOnly
                value={tpl}
                onSelectedBlockChange={setSelectedBlockId}
                expTpl={expTplMini}
                resources={expTpl.data.resources}
                anchorFields={anchorFields}
                validation={EMPTY_OBJ}
                showOnCompleteHookSettings={true}
                sendTestTaskButton={
                  <SendTestTaskButton d={expTpl} taskTpl={tpl} />
                }
                tokens={tokens}
                triggeredItems={triggeredItems}
                onGoToOtherItem={onGoToOtherItem}
              />
            </TabPanel>
            <TabPanel active={previewTab === 'people'}>
              <TaskExecutionReportTab
                tpl={expTpl}
                taskTplId={tpl.id}
                taskTplTitle={tpl.title}
              />
            </TabPanel>
            <TabPanel active={previewTab === 'notes'}>
              <TimelineItemNotesTab
                orgId={expTpl.data.orgId}
                experienceTemplateId={expTpl.id}
                itemId={tpl.id}
                itemType="TASK"
              />
            </TabPanel>
          </DialogContentWithoutPadding>
        </>
      )}
    </Dialog>
  );
};

export const AutomationTemplateReadOnlyDialog = ({
  open,
  onClose,
  tpl,
  expTpl,
  anchorFields,
  customFields,
  resources,
  onGoToOtherItem
}: {
  open: boolean;
  onClose: () => void;
  tpl: IAutomationTemplate;
  expTpl: Doc<IExperienceTemplate>;
  customFields: CustomUserField[];
  anchorFields: AnchorField[];
  resources: IExperienceTemplateResource[];
  onGoToOtherItem?: (item: ITimelineItem) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const [previewTab, setPreviewTab] = useTimelineDialogTabQueryParam();
  const expTplMini = useTaskBuilderContext(expTpl.data);
  const tokens = useTokenDefinitionsForExperience(
    expTplMini.supportingActors,
    customFields,
    { orgId: expTplMini.orgId, experienceTemplateId: expTplMini.templateId }
  );

  const { enabled: newUxEnabled } = useNewUx();

  const close = () => {
    setPreviewTab(null);
    onClose();
  };

  // Action selected in the viewer; carried into the edit URL by the Edit button.
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);

  // NEW_UX: the tabbed read-only dialog becomes the icon-rail automation viewer —
  // the Automation/People/Notes tabs are re-housed as rail sections. Classic
  // (newUx off) keeps the exact tabbed layout below.
  if (newUxEnabled) {
    return (
      <Dialog open={open} onClose={close} maxWidth={'xl'} fullWidth>
        {open && (
          <DialogContentWithoutPadding fullHeight>
            <AutomationViewerNewUxShell
              value={tpl}
              onSelectedActionChange={setSelectedActionId}
              expTpl={expTplMini}
              anchorFields={anchorFields}
              validation={EMPTY_OBJ}
              tokens={tokens}
              resources={resources}
              onCloseDialog={close}
              onGoToOtherItem={onGoToOtherItem}
              editButton={
                <GoToTimelineElementEditButton
                  expTpl={expTpl}
                  type="AUTOMATION"
                  elementId={tpl.id}
                  title={tpl.title}
                  selectedBlockId={selectedActionId}
                />
              }
              recipientsPanel={
                <AutomationExecutionReportTab
                  tpl={expTpl}
                  autoTplId={tpl.id}
                  autoTplTitle={tpl.title}
                  newUx
                />
              }
              notesPanel={
                <TimelineItemNotesTab
                  orgId={expTpl.data.orgId}
                  experienceTemplateId={expTpl.id}
                  itemId={tpl.id}
                  itemType="AUTOMATION"
                />
              }
            />
          </DialogContentWithoutPadding>
        )}
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={close} maxWidth={'xl'} fullWidth>
      {open && (
        <>
          <DialogActionsHeader
            hasVerticalPadding={false}
            onClose={close}
            left={
              <TimelineDialogTabs
                onChange={setPreviewTab}
                label={t('Automation')}
                previewTab={previewTab}
                elementId={tpl.id}
                title={tpl.title}
                type="AUTOMATION"
                selectedTab="Automation"
                experienceTemplateId={expTpl.id}
              />
            }
          >
            {previewTab === 'item' && (
              <GoToTimelineElementEditButton
                expTpl={expTpl}
                type="AUTOMATION"
                elementId={tpl.id}
                title={tpl.title}
                selectedBlockId={selectedActionId}
              />
            )}
            {isExportList && previewTab === 'people' && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileOutput size={16} />}
              >
                Export list
              </Button>
            )}
          </DialogActionsHeader>
          <DialogContentWithoutPadding fullHeight>
            <TabPanel active={previewTab === 'item'}>
              <AutomationBuilderReadOnly
                value={tpl}
                onSelectedActionChange={setSelectedActionId}
                expTpl={expTplMini}
                anchorFields={anchorFields}
                validation={EMPTY_OBJ}
                tokens={tokens}
                resources={resources}
              />
            </TabPanel>
            <TabPanel active={previewTab === 'people'}>
              <AutomationExecutionReportTab
                tpl={expTpl}
                autoTplId={tpl.id}
                autoTplTitle={tpl.title}
              />
            </TabPanel>
            <TabPanel active={previewTab === 'notes'}>
              <TimelineItemNotesTab
                orgId={expTpl.data.orgId}
                experienceTemplateId={expTpl.id}
                itemId={tpl.id}
                itemType="AUTOMATION"
              />
            </TabPanel>
          </DialogContentWithoutPadding>
        </>
      )}
    </Dialog>
  );
};

type TaskTimelineItemSelection =
  | { type: 'MSG'; item: ExperienceTemplateMessage }
  | {
      type: 'TASK';
      item: ITaskTemplate;
    }
  | { type: 'AUTOMATION'; item: IAutomationTemplate }
  | null;

export const useTaskTimelineItemSelection = (
  tpl: Doc<IExperienceTemplate>,
  queryParam = TIMELINE_ITEM_QUERY_PARAM
) => {
  const { tasks, automations, messages } = tpl.data;
  return useQueryParam<TaskTimelineItemSelection>(
    queryParam,
    (timelineItemId) => {
      const ref = parseTimelineItemIdParam(timelineItemId);
      const type = ref?.type || '';
      const id = ref?.id || '';
      if (type === 'task') {
        const task = tasks.find((t) => t.id === id) || null;
        return task ? { type: 'TASK', item: task } : null;
      }
      if (type === 'msg') {
        const msg =
          getAllMessageTemplates(messages).find((t) => t.id === id) || null;
        return msg ? { type: 'MSG', item: msg } : null;
      }
      if (type === 'automation') {
        const aut = automations.find((t) => t.id === id) || null;
        return aut ? { type: 'AUTOMATION', item: aut } : null;
      }
      return null;
    },
    (item) => {
      if (item?.type === 'MSG') {
        return toTimelineItemIdParam({ type: 'msg', id: item.item.id });
      }
      if (item?.type === 'TASK') {
        return toTimelineItemIdParam({ type: 'task', id: item.item.id });
      }
      if (item?.type === 'AUTOMATION') {
        return toTimelineItemIdParam({ type: 'automation', id: item.item.id });
      }
      return undefined;
    }
  );
};

type TaskTimelineProps = {
  tpl: Doc<IExperienceTemplate>;
  variant?: Variant;
  enrollStats?: boolean;
  hasZoomControls?: boolean;
  anchorFields: AnchorField[];
  onSelectItem?: (nextSelected: TaskTimelineItemSelection) => void;
};

// Branch the instance source at the component boundary (rules of hooks + no
// double-load). Pseudo-instances cover this whole component — stats AND the
// cohort annotations (anchorDate.at + status are on the projection).
export const TaskTimeline = (props: TaskTimelineProps) =>
  usePeopleTabV2Enabled() ? (
    <TaskTimelineV2 {...props} />
  ) : (
    <TaskTimelineV1 {...props} />
  );

const TaskTimelineV1 = (props: TaskTimelineProps) => {
  const [instances] = useExperienceInstancesCurrentlyRelevant(
    props.tpl.data.orgId,
    props.tpl.id,
    props.tpl.data.expectedEndThreshold
  );
  return <TaskTimelineInner instances={instances} {...props} />;
};

const TaskTimelineV2 = (props: TaskTimelineProps) => {
  const [instances] = useTimelineItemPseudoInstances(
    props.tpl.data.orgId,
    props.tpl.id,
    props.tpl.data.expectedEndThreshold
  );
  return <TaskTimelineInner instances={instances} {...props} />;
};

const TaskTimelineInner = ({
  instances,
  tpl,
  variant = 'normal',
  hasZoomControls = false,
  enrollStats,
  anchorFields,
  onSelectItem = NOOP
}: TaskTimelineProps & { instances: Doc<IExperience>[] | void }) => {
  const { t } = useTranslation();
  const { trackEvent } = useProductAnalytics();
  const { org } = useCurrentUser();
  const theme = useTheme();
  const [selectedActors, setSelectedActors] = useState<Set<string>>(new Set());
  // "Filter by Segment" — single-select and mutually exclusive with the actor
  // filter (each resets the other). Derived from the id so a segment that
  // disappears (saved segment deleted, last matching copy edited) silently
  // clears the filter instead of leaving stale state behind.
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const { segments: templateSegments } = useTemplateSegments(tpl.data);
  const selectedSegment = useMemo(
    () => templateSegments.find((s) => s.id === selectedSegmentId) || null,
    [templateSegments, selectedSegmentId]
  );
  // Ids of every item a member of the selected segment would receive; null
  // when no segment is active. Item ids double as timeline item keys.
  const segmentVisibleIds = useMemo(
    () =>
      selectedSegment
        ? getVisibleItemIdsForSegment(
            getTemplateAudienceCarriers(tpl.data),
            selectedSegment.rule
          )
        : null,
    [selectedSegment, tpl.data]
  );
  const { tasks, automations, anchor, tz } = tpl.data;
  const { t: ut } = useUnsafeTranslation();
  const { enabled: newUxEnabled } = useNewUx();
  const orgDateFormat = org.dateFormats.MMDDYYYY;
  // Stable so the memoised header doesn't re-render on unrelated state changes.
  const renderOuterUnit = useCallback(
    (i: number, gridOffset: number) =>
      newUxEnabled
        ? getWeekLabel(
            anchor,
            tz,
            orgDateFormat,
            i * 7 + Math.round(gridOffset / ONE_DAY),
            ut
          ).full
        : `${t('Week')} ${i + 1}`,
    [newUxEnabled, anchor, tz, orgDateFormat, ut, t]
  );
  // With the new UX off the process map keeps its default labels. The header
  // picks the full or short form by column width.
  const weekLabel = useCallback(
    (_weekIndex: number, weekStartDay: number) =>
      getWeekLabel(anchor, tz, orgDateFormat, weekStartDay, ut),
    [anchor, tz, orgDateFormat, ut]
  );
  const data = useMemo(
    () =>
      instances
        ? {
            instances,
            taskStats: getTaskStats(instances),
            automationStats: getAutomationsStats(instances)
          }
        : undefined,
    [instances]
  );
  const [scale, setScale] = useState(1);
  const actorDescriptors = useActorDescriptorsFromTemplate(
    tpl.data.supportingActors
  );
  const onScaleChangeRequest = useTaskTimelineScaleChangeRequest(setScale);

  // New icon-language timeline — additive, toggled locally, only offered on
  // the full report view (not the "dense" Overview preview card). Both
  // views render from the same template doc; nothing here affects the
  // classic path below when the toggle is off.
  const [useNewTimeline, setUseNewTimeline] = useState(false);
  // Neutral state is Overview: it fits the available width, so first load has
  // no horizontal scroll (and therefore no scrollbar-induced phantom vertical
  // scroll). Zooming to Week/Detail or expanding lanes brings scrollbars in
  // only when the content genuinely needs them.
  const [newTimelineView, setNewTimelineView] = useExperienceTimelineViewState({
    zoom: 0
  });
  // Populated by ExperienceTimeline's onEligibleLanesChange — collapse
  // eligibility depends on the packed layout, which only the canvas itself
  // computes; this is how the Controls row above it learns that.
  const [eligibleLaneIds, setEligibleLaneIds] = useState<string[]>([]);
  const [inspectedItem, setInspectedItem] = useTaskTimelineItemSelection(
    tpl,
    'timelineItemInspectId'
  );
  const items = useMemo(() => {
    const allMessages = getAllMessageTemplates(tpl.data.messages);

    const { connectUpIds, connectDownIds } = getMessageBatchConnections(tpl);
    const triggerHierarchyByEffects = getTriggerHierarchyByEffect(tpl);
    const triggerConnectors = getTriggerConnectors(tpl);

    return sortTaskTimelineItems(
      compact([
        ...allMessages.map<TimelineItem>((m) => {
          const causeItems = triggerHierarchyByEffects.messages[m.id] || [];
          const { start, end } = getTimelinePositionForMessageTempate(
            m.timing,
            tpl.data.tasks
          );
          return {
            key: m.id,
            start,
            end,
            data: { type: 'MESSAGE', actor: m.actor, batchMode: m.batchMode },
            parents: causeItems,
            body: (
              <MessageItem2
                tplContext={tpl.data}
                variant={variant}
                message={m}
                actor={actorDescriptors[m.actor]}
                onClick={() => onSelectItem({ type: 'MSG', item: m })}
                connectDown={connectDownIds.has(m.id)}
                connectLeft={triggerConnectors[m.id]?.left}
                connectRight={triggerConnectors[m.id]?.right}
              />
            )
          };
        }),
        ...tpl.data.tasks.map<TimelineItem>((t) => {
          const causeItems = triggerHierarchyByEffects.tasks[t.id] || [];
          const { start, end } = getTimelinePositionForTaskTemplate(
            t.timing,
            tpl.data.tasks
          );
          return {
            key: t.id,
            start,
            end,
            data: { type: 'TASK', actor: t.actor, batchMode: t.batchMode },
            parents: causeItems,
            body: (
              <TaskTimelineItemWithStats
                variant={variant}
                tpl={t}
                stats={data ? data.taskStats[t.id] || null : null}
                actor={actorDescriptors[t.actor]}
                onClick={() => onSelectItem({ type: 'TASK', item: t })}
                expTitle={tpl.data.title}
                orgName={org.name}
                connectUp={connectUpIds.has(t.id)}
                connectDown={connectDownIds.has(t.id)}
                connectLeft={triggerConnectors[t.id]?.left}
                connectRight={triggerConnectors[t.id]?.right}
              />
            )
          };
        }),
        ...tpl.data.automations.map<TimelineItem | null>((t) => {
          const causeItems = triggerHierarchyByEffects.automations[t.id] || [];
          const { start, end } = getTimelinePositionForAutomationTemplate(
            t.timing,
            tpl.data.tasks
          );
          return {
            key: t.id,
            start,
            end,
            data: { type: 'AUTOMATION' },
            parents: causeItems,
            body: (
              <AutomationTimelineItemWithSats
                tpl={t}
                variant={variant}
                stats={data ? data.automationStats[t.id] || null : null}
                onClick={() => onSelectItem({ type: 'AUTOMATION', item: t })}
                connectLeft={triggerConnectors[t.id]?.left}
                connectRight={triggerConnectors[t.id]?.right}
              />
            )
          };
        })
      ])
    );
  }, [tpl, data, variant, actorDescriptors, onSelectItem, org]);

  const annotations = useMemo<TimelineAnnotation[] | undefined>(() => {
    if (!data) {
      return undefined;
    }

    // find of tpl uses an anchor, if yes, group cohorts based on this anchor
    // find out where the anchor lives on the timeline
    // place the annotation in relative terms on this timeline
    // example:
    // anchor sits on day 21.
    // for a given cohort, this is the 22nd of march
    // the current day is 15th of march, 7 days before the anchor
    // the annotation will start at 21 - 7 = 14.

    const aggregation: {
      [start: string]: {
        start: number;
        end: number;
        instances: Doc<IExperience>[];
      };
    } = {};

    data.instances.forEach((inst) => {
      if (
        // inst.data.status === ExperienceStatus.COMPLETE ||
        inst.data.status === ExperienceStatus.ABORTED ||
        inst.data.status === ExperienceStatus.SUGGESTED
      ) {
        return;
      }
      const start = getAnnotationStart(inst, tpl);
      if (start < 0) {
        // Don't show items that are out of bounds -
        // we might add a placeholder for them later,
        // which collects all of them and mentions
        // that there are more people outside of the timeline
        return;
      }
      // Is this rounding still needed?
      const roundedStart = Math.floor(start / ONE_DAY) * ONE_DAY;
      const container = (aggregation[`${roundedStart}`] = aggregation[
        `${roundedStart}`
      ] || {
        start: roundedStart,
        end: roundedStart + ONE_DAY,
        instances: []
      });
      container.instances.push(inst);
    });

    return Object.values(aggregation).map((a) => ({
      key: `${a.start}`,
      start: a.start,
      end: a.end,
      body: (
        <Annotation
          instances={a.instances}
          tpl={tpl}
          anchorFields={anchorFields}
          variant={variant}
        />
      ),
      center: true
    }));
  }, [data, tpl, anchorFields, variant]);

  const expectedEnd =
    items.length > 0
      ? Math.max(
          ...items.map((i) => {
            if (i.data?.type === 'TASK') {
              return i.end;
            } else {
              return i.start;
            }
          })
        ) + tpl.data.expectedEndThreshold
      : 0;

  const anchorRulers = useAllRelevantAnchorRulers(
    anchor,
    anchorFields,
    t,
    expectedEnd,
    items.length > 0
  );

  const duration = getTemplateDuration(tpl);
  const showZoomControls =
    hasZoomControls && duration + tpl.data.expectedEndThreshold >= ONE_DAY * 14;
  const hasAutomation = items.some((a) => a.data?.type === 'AUTOMATION');

  const filteredItems = useMemo(() => {
    if (segmentVisibleIds) {
      return items.filter((x) => segmentVisibleIds.has(x.key));
    }
    if (!selectedActors.size) {
      return items;
    }
    return items.filter((x) => {
      if (x.data?.type === 'AUTOMATION') {
        return selectedActors.has('AUTOMATION');
      }
      return selectedActors.has(x.data?.actor);
    });
  }, [items, selectedActors, segmentVisibleIds]);

  const newTimelineModel = useExperienceTimelineModel(tpl, {
    orgName: org.name,
    anchorFields,
    showExpectedEndRuler: items.length > 0
  });

  const newTimelineFilteredItems = useMemo(() => {
    if (segmentVisibleIds) {
      return newTimelineModel.items.filter((x) => segmentVisibleIds.has(x.id));
    }
    if (!selectedActors.size) {
      return newTimelineModel.items;
    }
    return newTimelineModel.items.filter((x) => selectedActors.has(x.laneId));
  }, [newTimelineModel.items, selectedActors, segmentVisibleIds]);

  // Segment filter only: a lane whose every item was filtered out drops off
  // the canvas (the engine renders an empty band for every lane it is given).
  // The actor filter intentionally keeps today's behavior.
  const newTimelineVisibleLanes = useMemo(() => {
    if (!segmentVisibleIds) {
      return newTimelineModel.lanes;
    }
    const usedLaneIds = new Set(newTimelineFilteredItems.map((it) => it.laneId));
    return newTimelineModel.lanes.filter((l) => usedLaneIds.has(l.id));
  }, [newTimelineModel.lanes, newTimelineFilteredItems, segmentVisibleIds]);

  const newTimelineAnnotations = useMemo(() => {
    if (!data) {
      return [];
    }
    const aggregation: { [day: string]: { day: number; count: number; instances: Doc<IExperience>[] } } = {};
    data.instances.forEach((inst) => {
      if (
        inst.data.status === ExperienceStatus.ABORTED ||
        inst.data.status === ExperienceStatus.SUGGESTED
      ) {
        return;
      }
      const start = getAnnotationStart(inst, tpl);
      if (start < 0) {
        return;
      }
      const day = Math.floor(start / ONE_DAY);
      const container = (aggregation[`${day}`] = aggregation[`${day}`] || {
        day,
        count: 0,
        instances: []
      });
      container.count += 1;
      container.instances.push(inst);
    });
    const raw = Object.values(aggregation).map((a) => ({
      key: `${a.day}`,
      day: a.day,
      count: a.count,
      data: a.instances
    }));
    // merge adjacent buckets at Overview zoom (8px columns) so pills don't collide
    return mergeAnnotationBuckets<Doc<IExperience>>(raw, newTimelineView.zoom === 0 ? 4 : 1);
  }, [data, tpl, newTimelineView.zoom]);

  const newTimelineLaneLabelById = useMemo(
    () => Object.fromEntries(newTimelineModel.lanes.map((l) => [l.id, l.label])),
    [newTimelineModel.lanes]
  );

  const newTimelineTriggerMaps = useMemo(() => {
    const triggeredBy: { [toId: string]: string } = {};
    const triggersCount: { [fromId: string]: number } = {};
    newTimelineModel.triggers.forEach((tr) => {
      triggeredBy[tr.toId] = tr.fromId;
      triggersCount[tr.fromId] = (triggersCount[tr.fromId] || 0) + 1;
    });
    return { triggeredBy, triggersCount };
  }, [newTimelineModel.triggers]);

  const inspectedNewTimelineItem = useMemo(() => {
    if (!inspectedItem) return null;
    return (
      newTimelineModel.items.find((it) => it.id === inspectedItem.item.id) || null
    );
  }, [inspectedItem, newTimelineModel.items]);

  const trackFilterChange = (
    selected: ActorDescriptor[],
    deselected: ActorDescriptor[]
  ) => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_TPL,
      name: 'Timeline Filter Applied',
      Explanation:
        'Admin applied a filter to the content of the template timeline',
      'Experience Template ID': tpl.id,
      'Filter Type': 'actor',
      'Selected Filters': selected.map((a) => a.label).join(', ') || 'None',
      'Deselected Filters': deselected.map((a) => a.label).join(', ') || 'None'
    });
  };

  const trackSegmentFilterChange = (
    selected: SegmentDescriptor | null,
    deselected: SegmentDescriptor | null
  ) => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_TPL,
      name: 'Timeline Filter Applied',
      Explanation:
        'Admin applied a filter to the content of the template timeline',
      'Experience Template ID': tpl.id,
      'Filter Type': 'segment',
      'Selected Filters': selected?.label || 'None',
      'Deselected Filters': deselected?.label || 'None'
    });
  };

  const onSegmentFilterChange = (nextId: string | null) => {
    const next = templateSegments.find((s) => s.id === nextId) || null;
    // The two rows never combine: picking a segment restores every actor.
    setSelectedActors(new Set());
    setSelectedSegmentId(nextId);
    trackSegmentFilterChange(next, selectedSegment);
  };

  // const { onEditActor, onAddActor, onRemoveActor } = useMemo(() => {
  //   return {
  //     onEditActor: (nextActor: ISupportingActor, editedActor: string) => {
  //       updateDoc(tpl, () => ({
  //         supportingActors: {
  //           ...omit(tpl.data.supportingActors, [editedActor]),
  //           [nextActor.key]: nextActor
  //         }
  //       }));
  //     },
  //     onAddActor: (nextActor: ISupportingActor) => {
  //       updateDoc(tpl, () => ({
  //         supportingActors: {
  //           ...tpl.data.supportingActors,
  //           [nextActor.key]: nextActor
  //         }
  //       }));
  //     },
  //     onRemoveActor: (removedActor: string) =>
  //       updateDoc(tpl, () => ({
  //         supportingActors: {
  //           ...omit(tpl.data.supportingActors, [removedActor])
  //         }
  //       }))
  //   };
  // }, [tpl]);

  return (
    <>
      {variant === 'normal' ? (
        <TimelineModeSwitcher
          useNewTimeline={useNewTimeline}
          onChange={setUseNewTimeline}
        />
      ) : (
        <SubHeadlineContainer>
          <SubHeadLineItem>
            <List size="16" />
            {t('taskWithCount', { count: tasks.length })}{' '}
          </SubHeadLineItem>
          {!!automations.length && (
            <SubHeadLineItem>
              <Zap size="16" />
              {t('automationsWithCount', { count: automations.length })}{' '}
            </SubHeadLineItem>
          )}
          <SubHeadLineItem>
            <Calendar size="16" />
            {t('totalDuration')} {duration ? humanizeDuration(duration) : '-'}
          </SubHeadLineItem>
          {enrollStats && (
            <EnrollStats
              orgId={tpl.data.orgId}
              tplId={tpl.id}
              expectedEndThreshold={tpl.data.expectedEndThreshold}
            />
          )}
        </SubHeadlineContainer>
      )}
      <TimelineSectionContainer>
        {variant === 'normal' && (
          <>
            {/* <TemplateManageActorsButton
              tpl={tpl}
              onAdd={onAddActor}
              onEdit={onEditActor}
              onRemove={onRemoveActor}
            /> */}
            <FlexContainer
              justifyContent="flex-start"
              alignItems="flex-start"
              direction="column"
              spacing={1}
            >
              <Typography variant="h6" color="textSecondary">
                {t('Filter by Actor')}
              </Typography>

              <FlexContainer spacing={1}>
                <ActorLegendV2
                  descriptors={actorDescriptors}
                  selected={selectedActors}
                  setSelected={(nextSelected) => {
                    // The two rows never combine: touching the actor filter
                    // clears the segment filter.
                    setSelectedSegmentId(null);
                    const actors = Object.values(actorDescriptors);
                    if (hasAutomation) {
                      actors.push({
                        key: 'AUTOMATION',
                        label: 'Automation',
                        color: 'purple',
                        type: 'AUTOMATION'
                      });
                    }
                    const [selected, deselected] = partition(
                      actors,
                      (p) => !nextSelected.size || nextSelected.has(p.key)
                    );
                    trackFilterChange(selected, deselected);
                    setSelectedActors(nextSelected);
                  }}
                  hasAutomation={hasAutomation}
                  newStyle={useNewTimeline}
                />
              </FlexContainer>
            </FlexContainer>
            <SegmentFilterRow
              segments={templateSegments}
              selectedId={selectedSegmentId}
              onSelect={onSegmentFilterChange}
            />
            {useNewTimeline && (
              <ExperienceTimelineLegend
                participantColor={actorDescriptors[PARTICIPANT_ACTOR_KEY]?.color || theme.custom.colors.blue.w400}
                automationColor={theme.custom.colors.purple.w500}
              />
            )}
          </>
        )}
        {variant === 'normal' && useNewTimeline ? (
          <FlexContainer alignItems="flex-start" spacing={0} direction="column">
            <ExperienceTimelineControls
              zoom={newTimelineView.zoom}
              onZoomIn={() => setNewTimelineView(zoomInView)}
              onZoomOut={() => setNewTimelineView(zoomOutView)}
              showCollapseAll={eligibleLaneIds.length > 0}
              allCollapsed={
                eligibleLaneIds.length > 0 &&
                eligibleLaneIds.every((id) => newTimelineView.collapsedLanes[id])
              }
              onToggleCollapseAll={() =>
                setNewTimelineView((v) =>
                  setLanesCollapsedView(
                    v,
                    eligibleLaneIds,
                    !eligibleLaneIds.every((id) => v.collapsedLanes[id])
                  )
                )
              }
              showHideLabels={newTimelineView.zoom >= 1}
              hideLabels={newTimelineView.hideLabels}
              onToggleHideLabels={() => setNewTimelineView(toggleHideLabelsView)}
            />
            <FlexContainer alignItems="flex-start" spacing={0} fullWidth>
              {inspectedNewTimelineItem && (
                <TimelineInspectorRail
                  item={inspectedNewTimelineItem}
                  actorLabel={newTimelineLaneLabelById[inspectedNewTimelineItem.laneId] || ''}
                  triggeredByLabel={
                    newTimelineTriggerMaps.triggeredBy[inspectedNewTimelineItem.id]
                      ? newTimelineModel.items.find(
                          (it) => it.id === newTimelineTriggerMaps.triggeredBy[inspectedNewTimelineItem.id]
                        )?.label
                      : null
                  }
                  triggersCount={newTimelineTriggerMaps.triggersCount[inspectedNewTimelineItem.id]}
                  onClose={() => setInspectedItem(null)}
                  onView={() => onSelectItem(inspectedItem)}
                  canEdit={false}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <ExperienceTimeline
                  lanes={newTimelineVisibleLanes}
                  items={newTimelineFilteredItems}
                  bundles={newTimelineModel.bundles}
                  triggers={newTimelineModel.triggers}
                  rulers={anchorRulers.map((r) => ({
                    day: Math.round(r.position / ONE_DAY),
                    label: r.label,
                    emphasis: r.colorMode === 'light' ? ('plain' as const) : ('anchor' as const)
                  }))}
                  annotations={newTimelineAnnotations}
                  view={newTimelineView}
                  onViewChange={setNewTimelineView}
                  onEligibleLanesChange={setEligibleLaneIds}
                  onSelectItem={(id, meta) =>
                    setInspectedItem(meta as TaskTimelineItemSelection)
                  }
                  minDays={Math.ceil(getTemplateDuration(tpl) / ONE_DAY)}
                  weekLabel={newUxEnabled ? weekLabel : undefined}
                />
              </div>
            </FlexContainer>
          </FlexContainer>
        ) : (
          <Timeline
            items={filteredItems}
            innerUnitSpan={ONE_DAY * 1}
            outerUnitSpan={ONE_DAY * 7}
            renderOuterUnit={renderOuterUnit}
            grid={variant === 'normal'}
            header={variant === 'normal'}
            scale={scale}
            onScaleChangeRequest={
              hasZoomControls ? onScaleChangeRequest : undefined
            }
            annotations={annotations}
            rulers={variant === 'normal' ? anchorRulers : undefined}
            alwaysDrawAnnotationLane
          />
        )}
        {showZoomControls && !useNewTimeline && (
          <TaskTimelineZoomControlsContainer stickyOffset={32}>
            <TaskTimelineZoomControls value={scale} onChange={setScale} />
          </TaskTimelineZoomControlsContainer>
        )}
      </TimelineSectionContainer>
    </>
  );
};
